import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Box, Typography, Paper, useTheme, useMediaQuery } from '@mui/material';

function DailyProgressChart({ data = [], customColors }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Validação e formatação dos dados
  const chartData = React.useMemo(() => {
    // Log inicial dos dados recebidos
    console.log('DailyProgressChart - dados recebidos:', JSON.stringify(data, null, 2));

    if (!Array.isArray(data)) {
      console.warn('DailyProgressChart: dados inválidos');
      return [];
    }

    // Garantir que temos dados para todos os dias
    const fullData = Array.from({ length: 31 }, (_, index) => {
      const day = index + 1;
      const existingData = data.find(item => item && item.day === day);
      return {
        day,
        completion: existingData && typeof existingData.completion === 'number'
          ? Math.min(100, Math.max(0, existingData.completion))
          : 0
      };
    });

    // Log dos dados processados
    console.log('DailyProgressChart - dados processados:', JSON.stringify(fullData, null, 2));
    return fullData;
  }, [data]);

  // Se não houver dados válidos, mostra mensagem
  if (!chartData.length || !chartData.some(item => item.completion > 0)) {
    console.log('DailyProgressChart - sem dados válidos para exibir');
    return (
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" align="center">
          Sem dados disponíveis
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <ResponsiveContainer width="100%" height={180}>
        <AreaChart
          data={chartData}
          margin={{ top: 25, right: 10, left: 0, bottom: 20 }}
        >
          <defs>
            <linearGradient id="colorProgress" x1="0" y1="0" x2="0" y2="1">
              <stop 
                offset="5%" 
                stopColor={customColors?.primary || theme.palette.primary.main} 
                stopOpacity={0.3}
              />
              <stop 
                offset="95%" 
                stopColor={customColors?.primary || theme.palette.primary.main} 
                stopOpacity={0.1}
              />
            </linearGradient>
          </defs>
          <CartesianGrid 
            strokeDasharray="3 3"
            vertical={false}
            stroke={theme.palette.divider}
          />
          <XAxis
            dataKey="day"
            type="number"
            domain={[1, 31]}
            ticks={isMobile ? [1, 10, 20, 31] : [1, 5, 10, 15, 20, 25, 31]}
            tickFormatter={(value) => `Dia ${value}`}
            stroke={theme.palette.text.secondary}
            fontSize={12}
          />
          <YAxis
            domain={[0, 100]}
            ticks={[0, 25, 50, 75, 100]}
            tickFormatter={(value) => `${value}%`}
            stroke={theme.palette.text.secondary}
            fontSize={12}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 4
            }}
            formatter={(value) => [`${value}%`, 'Conclusão']}
            labelFormatter={(day) => `Dia ${day}`}
          />
          <Area
            type="monotone"
            dataKey="completion"
            stroke={customColors?.primary || theme.palette.primary.main}
            fill="url(#colorProgress)"
            strokeWidth={2}
            dot={{
              r: 3,
              fill: customColors?.primary || theme.palette.primary.main,
              stroke: theme.palette.background.paper,
              strokeWidth: 2
            }}
            activeDot={{
              r: 5,
              fill: customColors?.primary || theme.palette.primary.main,
              stroke: theme.palette.background.paper,
              strokeWidth: 2
            }}
            label={!isMobile ? {
              position: 'top',
              fill: theme.palette.text.secondary,
              fontSize: 11,
              formatter: (value) => `${value}%`
            } : null}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Paper>
  );
}

export default DailyProgressChart;
